import { useEffect, useMemo, useState } from 'react';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useApi, fetchApiRef } from '@backstage/core-plugin-api';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';

type Status = 'idle' | 'checking' | 'available' | 'exists' | 'error';

const normalizeHost = (host?: string) =>
  (host || '').replace(/^https?:\/\//, '').replace(/\/+$/, '');

const GitlabRepoPathFieldComponent = (props: any) => {
  const { onChange, rawErrors, formContext, uiSchema, schema, value } = props;
  const formData = (formContext && formContext.formData) || {};
  const fetchApi = useApi(fetchApiRef);

  const hostField = uiSchema?.['ui:options']?.hostField || 'gitHost';
  const groupField = uiSchema?.['ui:options']?.groupField || 'gitGroup';
  const groupPathOption = uiSchema?.['ui:options']?.groupPath;

  const host = normalizeHost(
    (uiSchema?.['ui:options']?.host as string) ||
      formData[hostField] ||
      schema?.host,
  );
  const group =
    groupPathOption ||
    formData[groupField] ||
    (schema as any)?.group ||
    '';
  const repo = value || '';

  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    let active = true;
    if (!host || !group || !repo) {
      setStatus('idle');
      setMessage(undefined);
      return () => {
        active = false;
      };
    }
    setStatus('checking');
    setMessage(undefined);

    const timer = setTimeout(() => {
      try {
        fetchApi
          .fetch(
            `/api/gitlab-exists?host=${encodeURIComponent(
              host,
            )}&fullPath=${encodeURIComponent(`${group}/${repo}`)}`,
          )
          .then(async resp => {
            if (!active) {
              return;
            }
            if (resp.ok) {
              const data = await resp.json();
              if (data.exists) {
                setStatus('exists');
                setMessage('이미 존재하는 리포지토리 경로입니다.');
              } else {
                setStatus('available');
                setMessage('사용 가능한 경로입니다.');
              }
            } else {
              setStatus('error');
              setMessage(`검증 실패(status ${resp.status})`);
            }
          })
          .catch(() => {
            if (!active) {
              return;
            }
            setStatus('error');
            setMessage('검증 중 오류가 발생했습니다.');
          });
      } catch (e) {
        if (!active) {
          return;
        }
        setStatus('error');
        setMessage('검증 중 오류가 발생했습니다.');
      }
    }, 400);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [host, group, repo, fetchApi]);

  const disabled = status === 'exists';
  const helper = useMemo(() => {
    if (message) return message;
    if (!host || !group)
      return 'GitLab Host/Group이 필요합니다 (자동 검증 대기).';
    return undefined;
  }, [host, group, message]);

  return (
    <TextField
      label={schema.title || 'Repository Name'}
      value={repo}
      onChange={e => onChange(e.target.value)}
      required={schema.required}
      error={!!rawErrors || status === 'exists' || status === 'error'}
      helperText={
        status === 'checking' ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <CircularProgress size="1rem" /> 경로 확인 중...
          </span>
        ) : (
          helper
        )
      }
      fullWidth
      disabled={disabled}
    />
  );
};

export const GitlabRepoPathFieldExtension = createScaffolderFieldExtension({
  name: 'GitlabRepoPath',
  component: GitlabRepoPathFieldComponent,
});

export default GitlabRepoPathFieldExtension;
